import React, { useMemo, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowRight, RefreshCw, LogIn } from 'lucide-react'
import { Card, CardHeader, CardTitle, CardContent, Button, ListPageSkeleton, ErrorAlert } from '@/components/ui'
import { Badge } from '@/components/ui/Badge'
import { PlansHeader } from '@/components/plans/PlansHeader'
import { BillingToggle } from '@/components/plans/BillingToggle'
import { PlanCard } from '@/components/plans/PlanCard'
import { Plan } from '@/types'
import { usePlans } from '@/hooks/usePlans'
import { getLocalizedPublicPath } from '@/lib/publicLocaleRouting'
import { cn } from '@/lib/utils'

const formatCurrency = (value: number, locale: string): string =>
  new Intl.NumberFormat(locale, { style: 'currency', currency: 'BRL', maximumFractionDigits: 2 }).format(value)

export const PlansPage: React.FC = () => {
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null)
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly')
  const navigate = useNavigate()
  const location = useLocation()
  const { t, i18n } = useTranslation()

  const { plans, isLoading, error, refetch } = usePlans()
  const locale = useMemo(() => i18n.resolvedLanguage || i18n.language || 'pt-BR', [i18n.language, i18n.resolvedLanguage])

  const handleSelectPlan = (plan: Plan): void => {
    setSelectedPlan(plan)
  }

  const getPrice = (plan: Plan): number => (billingCycle === 'yearly' ? plan.priceYearly : plan.priceMonthly)

  const getDiscount = (plan: Plan): number => {
    if (billingCycle !== 'yearly' || plan.priceYearly <= 0 || plan.priceMonthly <= 0) return 0
    const monthlyTotal = plan.priceMonthly * 12
    const discount = ((monthlyTotal - plan.priceYearly) / monthlyTotal) * 100
    return Math.max(0, Math.round(discount))
  }

  const getMaxDiscount = (): number => {
    if (billingCycle !== 'yearly' || plans.length === 0) return 0
    return Math.max(...plans.map((plan) => getDiscount(plan)))
  }

  const handleContinue = (): void => {
    if (!selectedPlan) return
    localStorage.setItem('selectedPlan', JSON.stringify(selectedPlan))
    navigate(getLocalizedPublicPath(location.pathname, '/register-dealership', i18n.resolvedLanguage || i18n.language))
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="mx-auto max-w-6xl px-4 py-6 sm:px-6 lg:px-8">
          <PlansHeader />
          <div className="mt-10">
            <ListPageSkeleton variant="cards" rows={3} showFilters={false} />
          </div>
        </div>
      </div>
    )
  }

  if (error && plans.length === 0) {
    return (
      <div className="min-h-screen bg-background">
        <div className="mx-auto flex min-h-screen max-w-3xl items-center px-4 py-8 sm:px-6 lg:px-8">
          <Card className="w-full border-border/70 shadow-xl">
            <CardHeader className="space-y-3">
              <Badge variant="danger" size="sm" className="w-fit">
                {t('publicPlans.errors.title')}
              </Badge>
              <CardTitle className="text-2xl text-foreground">
                {t('publicPlans.errors.title')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              <ErrorAlert type="error" message={error} />
              <Button onClick={refetch} className="inline-flex items-center gap-2">
                <RefreshCw className="h-4 w-4" strokeWidth={1.5} />
                {t('publicPlans.errors.retry')}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8 lg:py-10">
        {/* Hero header */}
        <PlansHeader />

        {/* Billing toggle */}
        <div className="mt-8 sm:mt-10">
          <BillingToggle value={billingCycle} onChange={setBillingCycle} discount={getMaxDiscount()} />
        </div>

        {/* Plan cards */}
        <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {plans.map((plan) => (
            <PlanCard
              key={plan.id}
              plan={plan}
              isSelected={selectedPlan?.id === plan.id}
              price={getPrice(plan)}
              discount={getDiscount(plan)}
              billingCycle={billingCycle}
              onSelect={handleSelectPlan}
            />
          ))}
        </div>

        {/* CTA section - desktop */}
        <div
          className={cn(
            'mt-8 hidden sm:flex items-center justify-between gap-4 rounded-2xl border border-border/70 bg-card px-6 py-5 shadow-sm transition-all duration-300',
            selectedPlan ? 'opacity-100 translate-y-0' : 'opacity-60',
          )}
        >
          <div className="min-w-0">
            {selectedPlan ? (
              <div className="space-y-2">
                <div className="flex items-center gap-3 flex-wrap">
                  <p className="text-sm text-muted-foreground">{t('publicPlans.summary.selectedPlan')}:</p>
                  <span className="text-base font-bold text-foreground">{selectedPlan.name}</span>
                  <span className="text-2xl font-bold tracking-tight text-foreground">
                    {formatCurrency(getPrice(selectedPlan), locale)}
                  </span>
                  <span className="text-sm text-muted-foreground">
                    {billingCycle === 'yearly'
                      ? t('publicPlans.summary.priceLabelYearly')
                      : t('publicPlans.summary.priceLabelMonthly')}
                  </span>
                </div>
                <div className="flex items-center gap-4 flex-wrap text-xs text-muted-foreground">
                  <span><span>{t('publicPlans.features.vehicles')}</span>: {selectedPlan.vehicleLimit}</span>
                  <span><span>{t('publicPlans.features.users')}</span>: {selectedPlan.userLimit}</span>
                  <span><span>{t('publicPlans.features.leads')}</span>: {selectedPlan.leadLimit}</span>
                  {selectedPlan.multipleLocations && (
                    <span>{t('publicPlans.features.multipleLocations')}</span>
                  )}
                </div>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">{t('publicPlans.summary.hint')}</p>
            )}
          </div>
          <button
            type="button"
            disabled={!selectedPlan}
            onClick={handleContinue}
            className={cn(
              'flex shrink-0 items-center gap-2 rounded-xl px-6 py-3 text-sm font-semibold transition-all duration-200',
              'active:scale-[0.98]',
              selectedPlan
                ? 'bg-accent text-accent-foreground shadow-md hover:opacity-90'
                : 'bg-muted text-muted-foreground cursor-not-allowed',
            )}
          >
            {t('publicPlans.actions.continue')}
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>

        {/* Login link */}
        <div className="mt-10 border-t border-border/70 pt-6 text-center">
          <p className="text-sm text-muted-foreground">
            {t('publicPlans.hero.alreadyHaveAccount')}{' '}
            <button
              type="button"
              onClick={() => navigate(getLocalizedPublicPath(location.pathname, '/login', i18n.resolvedLanguage || i18n.language))}
              className="inline-flex items-center gap-1 font-semibold text-accent underline underline-offset-4 hover:opacity-80 transition-opacity"
            >
              <LogIn className="h-3.5 w-3.5" />
              {t('publicPlans.hero.loginLink')}
            </button>
          </p>
        </div>
      </div>

      {/* Mobile sticky CTA */}
      <div
        className={cn(
          'fixed bottom-0 inset-x-0 z-50 border-t border-border bg-card/95 backdrop-blur-md px-4 py-3 sm:hidden transition-all duration-300',
          selectedPlan ? 'translate-y-0' : 'translate-y-full',
        )}
      >
        <div className="flex items-center gap-3">
          <div className="min-w-0 flex-1">
            <p className="text-xs text-muted-foreground truncate">{selectedPlan?.name}</p>
            <p className="text-lg font-bold text-foreground">
              {selectedPlan ? formatCurrency(getPrice(selectedPlan), locale) : ''}
              <span className="text-xs font-normal text-muted-foreground ml-1">
                {billingCycle === 'yearly'
                  ? t('publicPlans.summary.priceLabelYearly')
                  : t('publicPlans.summary.priceLabelMonthly')}
              </span>
            </p>
          </div>
          <button
            type="button"
            onClick={handleContinue}
            className="flex shrink-0 items-center gap-2 rounded-xl bg-accent text-accent-foreground px-5 py-3 text-sm font-semibold shadow-md active:scale-[0.98]"
          >
            {t('publicPlans.actions.continue')}
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  )
}
